import ArrowLeft from '@betfan/icons/linear/Arrows/AltArrowLeft.svg?react';
import { AuthologicApi } from 'api';
import { ArrowLeftIcon } from 'assets/icons';
import { RegistrationBanners } from 'features/Home';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { setDataLayer, setDataLayerWithCallback, ThirdPartyVerificationEvents, VerificationEvents } from 'shared/constants/datalayer';
import { useDeviceDetection } from 'shared/hooks/useDeviceDetection';
import { useNewMobileUiManager } from 'shared/hooks/useNewMobileUiManager';
import { isLoggedin } from 'shared/util/utility';
import { APP_URL, AWAITING_ADMIN_APPROVAL, SectionNavigation } from '../../shared/constants/common';
import { FeatureLoader, useMojeIdFeatureFlag } from '../../shared/featureFlags';
import { useIsLoggedIn } from '../../shared/hooks/useIsLoggedIn';
import useSectionNavigation from '../../shared/hooks/useSectionNavigation';
import { State } from '../../shared/interface';
import { OrModule } from './shared/OrModule';
import { animationDuration } from './shared/utils';
import { VerificationManual } from './shared/VerificationManual';
import { VerificationMojeId } from './shared/VerificationMojeId';

const VerificationMethod: React.FC = () => {
	const [expandMojeId, setExpandMojeId] = useState<boolean>(false);
	const verificationStatus = useSelector<State, string>((state) => state.login.accountDetail?.account?.verificationStatus);
	const device = useDeviceDetection();
	const isMobile = device === 'handheld';
	const login = useSelector<State, any>((state) => state.login);

	const history = useHistory();
	const location = useLocation();
	const { goToFirstNavigationView } = useSectionNavigation();
	const isLoggedIn = useIsLoggedIn();

	const { t } = useTranslation();

	const banners = useSelector<State, RegistrationBanners>((state) => state.home.registrationBanners);
	const mojeIdFeatureFlag = useMojeIdFeatureFlag();
	const showNewUI = useNewMobileUiManager();

	useEffect(() => {
		if (verificationStatus === AWAITING_ADMIN_APPROVAL) {
			history.push(APP_URL.home);
		}
	}, [verificationStatus]);

	useEffect(() => {
		if (!login?.accountDetail?.customerData?.personalIdentifier || !mojeIdFeatureFlag.value) {
			openVerificationForm();
		}
	}, [mojeIdFeatureFlag.value]);

	useEffect(() => {
		document.body.classList.add('hide-footer');
		return () => {
			document.body.classList.remove('hide-footer');
		};
	}, []);

	useEffect(() => {
		// FIXME The `useIsLoggedIn` hook doesn't work here. See https://betfan.atlassian.net/browse/BED-3086
		if (!isLoggedin()) {
			history.replace({ pathname: APP_URL.home });
			return;
		}
	}, [isLoggedin()]);

	const handleClose = () => {
		if (isMobile) {
			if (location.state) {
				history.goBack();
			} else {
				history.push(APP_URL.home);
			}
		} else {
			history.push({ ...location, hash: '' });
		}
	};

	const initializeMojeIdVerification = async () => {
		if (!isLoggedIn) {
			history.push(APP_URL.home);
			return;
		}

		const response = await AuthologicApi.verificationMojeId.initialize();

		setDataLayerWithCallback(ThirdPartyVerificationEvents.VERIFY_THIRD_PARTY_IN(response.data.id, 'mojeID')).finally(() => {
			window.location.href = response.data.url;
		});
	};

	const openVerificationForm = () => {
		if (!isLoggedIn) {
			history.push(APP_URL.home);
			return;
		}

		goToFirstNavigationView(SectionNavigation.ACCOUNT_VERIFICATION);

		setDataLayer(VerificationEvents.DATALAYER_VERIFICATION_FORM_START);
	};

	const handleMojeIdClick = () => {
		if (expandMojeId) {
			initializeMojeIdVerification();
		} else {
			setExpandMojeId(true);
		}
	};

	if (isMobile) {
		return (
			<>
				<div
					className="fixed pt-[calc(env(safe-area-inset-top)+55px)] bg-primary-700 inset-0 z-overlay w-full flex flex-col overflow-auto bg-no-repeat bg-cover bg-center"
					data-theme="light"
				>
					<section className="relative w-full h-screen bg-primary-700">
						<img
							className="fixed w-full bg-cover bg-center bg-repeat-y z-1 "
							src={banners.VERIFICATION_MOBILE_BACKGROUND}
							alt="verify_background"
							loading="lazy"
						/>
						<motion.div
							initial={{ marginTop: '13vw' }}
							animate={{ marginTop: expandMojeId ? '5vw' : '13vw' }}
							transition={{ duration: animationDuration }}
							className="relative px-4 pb-4 flex flex-col gap-2 z-[4] items-center justify-center max-w-[450px] mx-auto"
						>
							{login?.accountDetail?.customerData?.personalIdentifier != undefined && (
								<>
									<VerificationMojeId verifyMethodMojeId={handleMojeIdClick} />
									<OrModule />
								</>
							)}
							<VerificationManual expandMojeId={expandMojeId} openVerificationForm={openVerificationForm} />
						</motion.div>
					</section>
				</div>
				<div className="bg-primary-500 fixed top-0 left-0 right-0 z-overlay pt-safe text-white flex items-center  px-2">
					{showNewUI ? (
						<div className="py-4 relative w-full">
							<button data-cy="back-button" className="flex items-center gap-2 relative z-[1]" onClick={handleClose}>
								<ArrowLeftIcon />
								<div className="font-normal">{t('quit')}</div>
							</button>
							<div className="font-semibold absolute left-0 right-0 top-0 bottom-0 flex items-center justify-center">
								{t('registrationMethods_welcome')}
							</div>
						</div>
					) : (
						<div className="py-4 flex items-center">
							<button onClick={handleClose} data-cy="back-button">
								<ArrowLeft className="h-8" />
							</button>
							<div className="ml-3 font-medium">{t('registrationMethods_welcome')}</div>
						</div>
					)}
				</div>
			</>
		);
	}

	return (
		<section className="bg-primary-700 inset-0 w-full flex flex-col fixed overflow-y-auto" data-theme="light">
			<img
				className={'fixed  w-full top-10  bg-cover  bg-center z-0 '}
				src={banners.VERIFICATION_DESKTOP}
				alt="verif_background"
				loading="lazy"
			/>
			<motion.div
				className="ml-[62%] w-[320px] pb-4 relative z-1"
				initial={{ marginTop: '7%' }}
				animate={{ marginTop: expandMojeId ? '7%' : '10%' }}
				transition={{ duration: 0.3 }}
			>
				<div className={'relative flex flex-col gap-2 z-[4]'}>
					{login?.accountDetail?.customerData?.personalIdentifier != undefined && (
						<>
							<VerificationMojeId verifyMethodMojeId={handleMojeIdClick} />
							<OrModule />
						</>
					)}
					<VerificationManual expandMojeId={expandMojeId} openVerificationForm={openVerificationForm} />
				</div>
			</motion.div>
		</section>
	);
};

export const VerificationMethodV2 = () => (
	<FeatureLoader>
		<VerificationMethod />
	</FeatureLoader>
);
